import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Settings, Package, Tag, Link2, Shield, Bell, Download, BookOpen, CreditCard, Sparkles, ArrowRight, CheckCircle, AlertCircle, Users } from 'lucide-react';

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function AdminConfigurationHub() {
  const [settings, setSettings] = useState(null);
  const [productCount, setProductCount] = useState(0);
  const token = sessionStorage.getItem('ose_auth') || localStorage.getItem('ose_auth');
  const H = { Authorization: `Bearer ${token}` };

  const load = useCallback(async () => {
    try {
      const [s, p] = await Promise.all([
        fetch(`${BACKEND}/api/parametres`).then(r => r.json()).catch(() => ({})),
        fetch(`${BACKEND}/api/products?limit=1`).then(r => r.json()).catch(() => ({})),
      ]);
      setSettings(s);
      setProductCount(p.total || 0);
    } catch { /* silent */ }
  // eslint-disable-next-line
  }, []);

  useEffect(() => { load(); }, [load]);

  const modules = [
    {
      to: '/admin/catalogue', icon: <Package size={18}/>, color: 'var(--admin-teal)',
      label: 'Catalogue location',
      desc: `${productCount} produits actifs`,
      status: 'ok',
    },
    {
      to: '/admin/tarifs', icon: <Tag size={18}/>, color: 'var(--admin-gold)',
      label: 'Tarifs par profil',
      desc: 'Particulier, Pro, Asso, CE',
      status: 'ok',
    },
    {
      to: '/admin/partenaires-config', icon: <Link2 size={18}/>, color: 'var(--admin-blue)',
      label: 'Prestataires',
      desc: 'Gérer les partenaires et leurs produits',
      status: 'ok',
    },
    {
      to: '/admin/parametres', icon: <CreditCard size={18}/>, color: 'var(--admin-green)',
      label: 'Stripe & Paiements',
      desc: settings?.stripe_mode === 'live' ? '✅ Mode live actif' : '⚠️ Mode test',
      status: settings?.stripe_mode === 'live' ? 'ok' : 'warn',
    },
    {
      to: '/admin/ia-agent', icon: <Sparkles size={18}/>, color: 'var(--admin-purple)',
      label: 'IA Catalogue',
      desc: 'Génération automatique de descriptions',
      status: 'ok',
    },
    {
      to: '/admin/blog', icon: <BookOpen size={18}/>, color: 'var(--admin-amber)',
      label: 'Blog & SEO',
      desc: 'Articles et pages thématiques',
      status: 'ok',
    },
    {
      to: '/admin/securite', icon: <Shield size={18}/>, color: 'var(--admin-red)',
      label: 'Sécurité & Accès',
      desc: 'Sauvegardes, 2FA, journaux',
      status: 'ok',
    },
    {
      to: '/admin/utilisateurs', icon: <Users size={18}/>, color: 'var(--admin-gold)',
      label: 'Utilisateurs & accès',
      desc: 'Comptes admin, livreur, clients',
      status: 'ok',
    },
    {
      to: '/admin/notifications', icon: <Bell size={18}/>, color: 'var(--admin-teal)',
      label: 'Notifications',
      desc: 'Alertes et emails automatiques',
      status: 'ok',
    },
    {
      to: '/admin/exports', icon: <Download size={18}/>, color: '#9CA3AF',
      label: 'Exports',
      desc: 'Export CSV, iCal, rapports',
      status: 'ok',
    },
  ];

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 24 }}>
      <div>
        <h1 className="text-2xl font-bold mb-1" style={{ color: 'var(--admin-blue)' }}>⚙️ Configuration</h1>
        <p className="text-sm" style={{ color: 'var(--admin-muted)' }}>Catalogue, tarifs, Stripe, sécurité et paramètres</p>
      </div>

      {/* Statut Stripe */}
      {settings && (
        <div className="flex items-center gap-3 p-4 rounded-2xl"
          style={{ background: settings.stripe_mode === 'live' ? 'rgba(31,157,122,0.08)' : 'rgba(197,139,25,0.08)', border: `1px solid ${settings.stripe_mode === 'live' ? '#1F9D7A' : '#C58B19'}30` }}>
          {settings.stripe_mode === 'live'
            ? <CheckCircle size={18} style={{ color: 'var(--admin-green)' }} />
            : <AlertCircle size={18} style={{ color: 'var(--admin-amber)' }} />}
          <div>
            <p className="text-sm font-semibold" style={{ color: 'var(--admin-text)' }}>
              Stripe : {settings.stripe_mode === 'live' ? 'Mode live actif — paiements réels' : 'Mode test actif — aucun paiement réel'}
            </p>
            <p className="text-xs" style={{ color: 'var(--admin-muted)' }}>Boutique : {settings.boutique_active ? 'En ligne' : 'Hors ligne'}</p>
          </div>
          <Link to="/admin/parametres" className="ml-auto text-xs px-3 py-1.5 rounded-lg" style={{ background: 'rgba(255,255,255,0.06)', color: 'var(--admin-text)' }}>Configurer</Link>
        </div>
      )}

      {/* Modules grille */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {modules.map((m, i) => (
          <Link key={i} to={m.to}
            className="flex items-start gap-3 p-4 rounded-2xl transition-all hover:opacity-90"
            style={{ background: 'var(--admin-surface)', border: `1px solid ${m.color}20` }}>
            <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: `${m.color}15`, color: m.color }}>{m.icon}</div>
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-sm" style={{ color: 'var(--admin-text)' }}>{m.label}</p>
              <p className="text-xs mt-0.5" style={{ color: 'var(--admin-muted)' }}>{m.desc}</p>
            </div>
            <ArrowRight size={14} style={{ color: 'var(--admin-faint)', flexShrink: 0 }} />
          </Link>
        ))}
      </div>
    </div>
  );
}
